import React from "react";
import { Button } from "@/components/ui/button";
import { Download, FileText, ShieldCheck } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

// ASCII-safe Turkish (jsPDF default Helvetica)
function tr(s) {
  if (s === undefined || s === null) return "-";
  return String(s)
    .replace(/ş/g, "s").replace(/Ş/g, "S")
    .replace(/ı/g, "i").replace(/İ/g, "I")
    .replace(/ğ/g, "g").replace(/Ğ/g, "G")
    .replace(/ü/g, "u").replace(/Ü/g, "U")
    .replace(/ö/g, "o").replace(/Ö/g, "O")
    .replace(/ç/g, "c").replace(/Ç/g, "C");
}

function drawSectionHeader(doc, title, y, pageW) {
  doc.setFillColor(10, 10, 10);
  doc.rect(40, y, pageW - 80, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(title, 50, y + 15);
  doc.setTextColor(10, 10, 10);
  return y + 30;
}

function drawLineChart(doc, x, y, w, h, data, options) {
  const {
    series, // [{key, color, dashed}]
    xLabel = "", yLabel = "", title = "",
  } = options;

  // Background
  doc.setFillColor(250, 250, 250);
  doc.rect(x, y, w, h, "F");
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(0.5);
  doc.rect(x, y, w, h);

  // Title
  if (title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(title, x + 6, y + 12);
  }

  const padL = 36, padR = 8, padT = title ? 20 : 8, padB = 22;
  const plotX = x + padL;
  const plotY = y + padT;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  // Compute y range
  let minY = Infinity, maxY = -Infinity;
  series.forEach(s => {
    data.forEach(d => {
      const v = d[s.key];
      if (typeof v === "number") {
        if (v < minY) minY = v;
        if (v > maxY) maxY = v;
      }
    });
  });
  if (!isFinite(minY)) { minY = 0; maxY = 1; }
  if (minY === maxY) { minY -= 1; maxY += 1; }
  const pad = (maxY - minY) * 0.1;
  minY -= pad; maxY += pad;

  // Grid + Y axis labels (4 ticks)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(82, 82, 91);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  for (let i = 0; i <= 4; i++) {
    const yy = plotY + (plotH / 4) * i;
    doc.line(plotX, yy, plotX + plotW, yy);
    const val = maxY - ((maxY - minY) / 4) * i;
    doc.text(val.toFixed(0), x + 4, yy + 3);
  }

  // X labels (point names)
  doc.setDrawColor(180, 180, 180);
  const n = data.length;
  if (n > 1) {
    data.forEach((d, i) => {
      const xx = plotX + (plotW / (n - 1)) * i;
      doc.line(xx, plotY, xx, plotY + plotH);
      const lbl = String(d.point || "");
      doc.text(lbl.length > 8 ? lbl.slice(0, 8) : lbl, xx - 14, plotY + plotH + 12);
    });
  }

  // Plot series
  series.forEach(s => {
    const color = s.color || [10, 10, 10];
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(s.dashed ? 0.6 : 1.2);
    if (s.dashed) doc.setLineDashPattern([3, 3], 0); else doc.setLineDashPattern([], 0);
    let prev = null;
    data.forEach((d, i) => {
      const v = d[s.key];
      if (typeof v !== "number") return;
      const px = plotX + (plotW / (Math.max(1, n - 1))) * i;
      const py = plotY + plotH - ((v - minY) / (maxY - minY)) * plotH;
      if (prev) doc.line(prev.x, prev.y, px, py);
      prev = { x: px, y: py };
    });
    doc.setLineDashPattern([], 0);
    // Dots
    if (!s.dashed) {
      doc.setFillColor(color[0], color[1], color[2]);
      data.forEach((d, i) => {
        const v = d[s.key];
        if (typeof v !== "number") return;
        const px = plotX + (plotW / (Math.max(1, n - 1))) * i;
        const py = plotY + plotH - ((v - minY) / (maxY - minY)) * plotH;
        doc.circle(px, py, 1.5, "F");
      });
    }
  });

  // Legend
  doc.setFontSize(7);
  doc.setTextColor(10, 10, 10);
  let lx = x + padL;
  const ly = y + h - 4;
  series.forEach((s) => {
    doc.setFillColor(s.color[0], s.color[1], s.color[2]);
    doc.rect(lx, ly - 6, 8, 4, "F");
    doc.text(s.label || s.key, lx + 12, ly - 2);
    lx += doc.getTextWidth(s.label || s.key) + 30;
  });
  doc.setTextColor(10, 10, 10);
}

export default function ExportBar({ project, climate, layers, metrics, conditions, advice }) {
  const { t } = useI18n();
  const generatePDF = () => {
    if (!metrics) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const certId = "TR-ISI-" + Date.now().toString(36).toUpperCase();
    const dateStr = project.date || new Date().toISOString().slice(0, 10);

    // =============== PAGE 1: COVER ===============
    // Full black background
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, pageW, pageH, "F");

    // Neon cyan accent bar
    doc.setFillColor(0, 229, 255);
    doc.rect(0, 100, pageW, 2, "F");
    doc.rect(0, pageH - 100, pageW, 2, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("TS 825:2024 / ISO 6946 / EN 13788", 40, 70);

    doc.setFontSize(40);
    doc.text("ISI", 40, 240);
    doc.text("YALITIMI", 40, 285);
    doc.setTextColor(0, 229, 255);
    doc.text("HESAP RAPORU", 40, 330);
    doc.setTextColor(255, 255, 255);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("Heat Insulation Calculation Report", 40, 350);

    // Project name large
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(tr(project.project_name) || "Proje", 40, 420);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Musteri: ${tr(project.customer) || "-"}`, 40, 442);
    doc.text(`Bas Muhendis: ${tr(project.engineer) || "-"}`, 40, 458);
    doc.text(`Tarih: ${dateStr}`, 40, 474);
    doc.text(`Yapi Elemani: ${tr(project.building_element)}`, 40, 490);
    doc.text(`Sehir/Ilce: ${tr(climate?.city)} / ${tr(climate?.district)}`, 40, 506);
    doc.text(`Iklim Bolgesi: ${tr(climate?.climate_zone_label || project.climate_zone)}`, 40, 522);

    // Right side cert box
    doc.setDrawColor(0, 229, 255);
    doc.setLineWidth(1.5);
    doc.rect(pageW - 220, 200, 180, 320);
    doc.setTextColor(0, 229, 255);
    doc.setFontSize(8);
    doc.text("BELGE / CERT. ID", pageW - 210, 224);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(certId, pageW - 210, 246);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 229, 255);
    doc.setFontSize(8);
    doc.text("BAS METRIKLER", pageW - 210, 290);

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("U-Degeri", pageW - 210, 314);
    doc.setFontSize(22);
    doc.setTextColor(0, 229, 255);
    doc.text(metrics.u_value.toFixed(3), pageW - 210, 340);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("W/m2K", pageW - 210, 352);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("R-Degeri", pageW - 210, 380);
    doc.setFontSize(22);
    doc.setTextColor(57, 255, 20);
    doc.text(metrics.total_r.toFixed(3), pageW - 210, 406);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("m2K/W", pageW - 210, 418);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Yogusma Riski", pageW - 210, 446);
    doc.setFontSize(14);
    if (metrics.condensation_risk) {
      doc.setTextColor(255, 59, 48);
      doc.text("RISKLI", pageW - 210, 468);
    } else {
      doc.setTextColor(57, 255, 20);
      doc.text("YOK", pageW - 210, 468);
    }
    doc.setTextColor(255, 255, 255);

    // Academic verification badge bottom-left
    doc.setDrawColor(0, 229, 255);
    doc.setLineWidth(0.8);
    doc.circle(80, pageH - 60, 30);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 229, 255);
    doc.text("ACADEMIC", 80, pageH - 65, { align: "center" });
    doc.text("VERIFIED", 80, pageH - 56, { align: "center" });
    doc.text("TS 825", 80, pageH - 47, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("Verified under academic research supervision", 130, pageH - 60);
    doc.text("Akademik arastirma gozetiminde TS 825:2024 uyumlulugu", 130, pageH - 48);

    // =============== PAGE 2: TABLE OF CONTENTS ===============
    doc.addPage();
    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("ICINDEKILER", 40, 70);
    doc.setLineWidth(2);
    doc.setDrawColor(0, 229, 255);
    doc.line(40, 78, 200, 78);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const toc = [
      ["1.", "Kapak Sayfasi", "1"],
      ["2.", "Icindekiler", "2"],
      ["3.", "Materyal ve Yontem", "3"],
      ["4.", "Katman Analizi ve Metrikler", "4"],
      ["5.", "Glaser Yontem Grafikleri (Sicaklik & Buhar Basinci)", "5"],
      ["6.", "Yogusma Analizi", "5"],
      advice ? ["7.", "AI Uzman Tavsiyesi (Onerilen Malzemeler, Zayifliklar, Azaltma Yontemleri)", "6"] : null,
      [advice ? "8." : "7.", "Akademik Dogrulama ve Imza", "Son"],
    ].filter(Boolean);
    let y = 110;
    toc.forEach((row) => {
      doc.setFont("helvetica", "bold");
      doc.text(row[0], 40, y);
      doc.setFont("helvetica", "normal");
      doc.text(row[1], 70, y);
      doc.setDrawColor(212, 212, 212);
      doc.setLineDashPattern([1, 2], 0);
      doc.line(70 + doc.getTextWidth(row[1]) + 4, y - 2, pageW - 70, y - 2);
      doc.setLineDashPattern([], 0);
      doc.text(row[2], pageW - 60, y);
      y += 22;
    });

    // =============== PAGE 3: MATERIALS & METHODS ===============
    doc.addPage();
    y = 50;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("MATERYAL VE YONTEM", 40, y);
    doc.setLineWidth(2);
    doc.setDrawColor(0, 229, 255);
    doc.line(40, y + 6, 250, y + 6);
    y += 30;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Standartlar", 40, y); y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const standards = [
      "TS 825:2024 - Binalarda Isi Yalitim Kurallari",
      "ISO 6946 - Yapi bilesenleri ve yapi elemanlari icin termal direnc ve isi gecirgenligi hesaplama",
      "EN 13788 - Bina bilesenleri ve bina elemanlarinin higrothermal performansi (Glaser yontemi)",
    ];
    standards.forEach((s) => {
      const tl = doc.splitTextToSize("- " + s, pageW - 80);
      doc.text(tl, 50, y);
      y += tl.length * 12 + 2;
    });
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Hesaplama Yontemi", 40, y); y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const methods = [
      "Toplam Termal Direnc: R_total = R_si + Σ(d_i / λ_i) + R_se, R_si=0.13, R_se=0.04 m²K/W (dis duvar)",
      "Isi Gecirgenligi: U = 1 / R_total (W/m²K)",
      "Sicaklik Dagilimi: T_i+1 = T_i - (R_i / R_total) · ΔT",
      "Doymus Buhar Basinci (Magnus): p_sat(T) = 610.78 · exp(17.27·T / (T + 237.3))",
      "Glaser - Buhar Basinci Gradyani: p(x) = p_i - (s_d(x) / Σs_d) · (p_i - p_o), s_d = μ · d",
      "Iclerdeki Yogusma: p_vapor(x) > p_sat(T(x)) noktalarinda interstitial yogusma riski",
      "Yillik Isitma Enerjisi (referans): Q = U · A · HDD · 24 / 1000 (kWh/yil)",
    ];
    methods.forEach((s) => {
      const tl = doc.splitTextToSize("- " + s, pageW - 80);
      if (y + tl.length * 12 > pageH - 60) { doc.addPage(); y = 50; }
      doc.text(tl, 50, y);
      y += tl.length * 12 + 2;
    });
    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Sinir Kosullari", 40, y); y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const bounds = [
      `Ic ortam: T = ${conditions.indoor_temp} °C, RH = ${conditions.indoor_humidity} %`,
      `Dis ortam: T = ${conditions.outdoor_temp} °C, RH = ${conditions.outdoor_humidity ?? 80} %`,
      `Konum: ${tr(climate?.city)} / ${tr(climate?.district)} (Bolge ${climate?.zone || "-"})`,
      `Isitma Derece Gunleri (HDD): ${climate?.hdd ?? "-"}, Sogutma (CDD): ${climate?.cdd ?? "-"}`,
    ];
    bounds.forEach((s) => {
      doc.text("- " + s, 50, y);
      y += 14;
    });

    // =============== PAGE 4: METRICS + LAYERS ===============
    doc.addPage();
    y = 50;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("KATMAN ANALIZI VE METRIKLER", 40, y);
    doc.setLineWidth(2);
    doc.setDrawColor(0, 229, 255);
    doc.line(40, y + 6, 300, y + 6);
    y += 30;

    const cellW = (pageW - 80) / 4;
    const m = [
      ["TOPLAM R", metrics.total_r.toFixed(3), "m2K/W"],
      ["U-DEGERI", metrics.u_value.toFixed(3), "W/m2K"],
      ["CIY NOKTASI", metrics.dew_point.toFixed(2), "°C"],
      ["YOGUSMA", metrics.condensation_risk ? "RISKLI" : "OK", ""],
    ];
    m.forEach((row, i) => {
      const x = 40 + i * cellW;
      doc.setFillColor(245, 245, 245);
      doc.rect(x, y, cellW, 70, "F");
      doc.setDrawColor(10, 10, 10);
      doc.setLineWidth(0.5);
      doc.rect(x, y, cellW, 70);
      doc.setTextColor(82, 82, 91);
      doc.setFontSize(7);
      doc.text(row[0], x + 10, y + 18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(10, 10, 10);
      doc.text(row[1], x + 10, y + 48);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(82, 82, 91);
      doc.text(row[2], x + 10, y + 62);
    });
    y += 90;
    doc.setTextColor(10, 10, 10);

    autoTable(doc, {
      startY: y,
      head: [["#", "Malzeme", "Marka", "Kalinlik (m)", "lambda (W/mK)", "mu", "R (m2K/W)", "sd (m)"]],
      body: layers.map((l, i) => [
        "L" + (i + 1),
        tr(l.name),
        tr(l.brand) || "Jenerik",
        l.thickness.toFixed(3),
        l.lambda_value.toFixed(3),
        l.mu ?? 1,
        (metrics.layer_r[i] ?? 0).toFixed(4),
        (metrics.layer_sd?.[i] ?? 0).toFixed(3),
      ]),
      theme: "grid",
      headStyles: { fillColor: [10, 10, 10], textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { font: "helvetica", fontSize: 9 },
      margin: { left: 40, right: 40 },
    });

    // =============== PAGE 5: GLASER CHARTS ===============
    doc.addPage();
    y = 50;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(10, 10, 10);
    doc.text("GLASER YONTEM GRAFIKLERI", 40, y);
    doc.setLineWidth(2);
    doc.setDrawColor(0, 229, 255);
    doc.line(40, y + 6, 290, y + 6);
    y += 30;

    const labels = ["Ic Hava", "Ic Yuzey"];
    for (let i = 0; i < layers.length - 1; i++) labels.push(`L${i+1}/L${i+2}`);
    labels.push("Dis Yuzey");

    const tempData = (metrics.temperatures || []).map((t, i) => ({
      point: labels[i] || `P${i}`,
      temp: Number(t),
      dew: Number(metrics.dew_point),
    }));
    drawLineChart(doc, 40, y, pageW - 80, 200, tempData, {
      title: "Sicaklik Dagilimi (°C)",
      series: [
        { key: "temp", label: "Sicaklik", color: [255, 59, 48] },
        { key: "dew", label: "Ciy Noktasi", color: [0, 229, 255], dashed: true },
      ],
    });
    y += 220;

    const pData = (metrics.vapor_pressure || []).map((v, i) => ({
      point: labels[i] || `P${i}`,
      vp: Number(v),
      sp: Number(metrics.saturation_pressure?.[i] ?? 0),
    }));
    drawLineChart(doc, 40, y, pageW - 80, 200, pData, {
      title: "Buhar Basinci Gradyani (Pa)",
      series: [
        { key: "vp", label: "Buhar Basinci", color: [0, 229, 255] },
        { key: "sp", label: "Doymus Basinc", color: [57, 200, 20], dashed: true },
      ],
    });
    y += 230;

    // Condensation summary
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("YOGUSMA ANALIZI OZETI", 40, y); y += 14;
    autoTable(doc, {
      startY: y,
      head: [["Yuzey Yogusma", "Ara Katman Yogusma", "Risk Noktasi", "Ic Yuzey °C", "Ciy °C", "Σsd"]],
      body: [[
        metrics.surface_condensation_risk ? "RISK" : "OK",
        metrics.interstitial_condensation_risk ? "RISK" : "OK",
        (metrics.condensation_indices || []).length,
        metrics.interior_surface_temp.toFixed(2),
        metrics.dew_point.toFixed(2),
        metrics.total_sd?.toFixed(2),
      ]],
      theme: "grid",
      headStyles: { fillColor: [10, 10, 10], textColor: 255, fontStyle: "bold", fontSize: 8 },
      styles: { font: "helvetica", fontSize: 9 },
      margin: { left: 40, right: 40 },
    });

    // =============== PAGE 6: AI ADVICE ===============
    if (advice) {
      doc.addPage();
      y = 50;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("AI UZMAN TAVSIYESI", 40, y);
      doc.setLineWidth(2);
      doc.setDrawColor(0, 229, 255);
      doc.line(40, y + 6, 220, y + 6);
      y += 30;

      doc.setFontSize(11);
      doc.text("Performans Puani: " + (advice.puan ?? "-") + " / 100", 40, y); y += 16;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const summary = doc.splitTextToSize(tr(advice.ozet || ""), pageW - 80);
      doc.text(summary, 40, y); y += summary.length * 12 + 10;

      if (advice.oneri_malzemeler?.length) {
        if (y > pageH - 80) { doc.addPage(); y = 50; }
        y = drawSectionHeader(doc, "ONERILEN MALZEMELER", y, pageW);
        doc.setFontSize(9.5);
        advice.oneri_malzemeler.forEach((r) => {
          const t = `- ${tr(r.isim)}: ${tr(r.neden)} (${tr(r.tahmini_iyilesme || "")})`;
          const tl = doc.splitTextToSize(t, pageW - 80);
          if (y + tl.length * 12 > pageH - 60) { doc.addPage(); y = 50; }
          doc.text(tl, 44, y); y += tl.length * 12 + 2;
        });
        y += 6;
      }

      if (advice.dezavantajlar?.length) {
        if (y > pageH - 80) { doc.addPage(); y = 50; }
        y = drawSectionHeader(doc, "ZAYIFLIKLAR & AZALTMA YONTEMLERI", y, pageW);
        doc.setFontSize(9.5);
        advice.dezavantajlar.forEach((d) => {
          if (y > pageH - 70) { doc.addPage(); y = 50; }
          doc.setFont("helvetica", "bold");
          doc.text(`[${tr(d.malzeme)}] ${tr(d.zayiflik)}`, 44, y); y += 12;
          doc.setFont("helvetica", "normal");
          const ex = doc.splitTextToSize(tr(d.aciklama || ""), pageW - 100);
          doc.text(ex, 56, y); y += ex.length * 12 + 2;
          (d.azaltma_yontemleri || []).forEach((s) => {
            const sl = doc.splitTextToSize("> " + tr(s), pageW - 120);
            if (y + sl.length * 12 > pageH - 60) { doc.addPage(); y = 50; }
            doc.text(sl, 64, y); y += sl.length * 12 + 1;
          });
          if (d.tamamlayici_malzemeler?.length) {
            const tm = doc.splitTextToSize("Tamamlayici: " + d.tamamlayici_malzemeler.map(tr).join(", "), pageW - 120);
            doc.setTextColor(82, 82, 91);
            doc.text(tm, 64, y); y += tm.length * 12 + 2;
            doc.setTextColor(10, 10, 10);
          }
          y += 4;
        });
      }

      if (advice.uygulama_ipuclari?.length) {
        if (y > pageH - 80) { doc.addPage(); y = 50; }
        y = drawSectionHeader(doc, "UYGULAMA IPUCLARI", y, pageW);
        doc.setFontSize(9.5);
        advice.uygulama_ipuclari.forEach((t) => {
          const tl = doc.splitTextToSize("- " + tr(t), pageW - 80);
          if (y + tl.length * 12 > pageH - 60) { doc.addPage(); y = 50; }
          doc.text(tl, 44, y); y += tl.length * 12 + 2;
        });
      }
    }

    // =============== FINAL: CERTIFICATION ===============
    const lastPage = doc.internal.getNumberOfPages();
    doc.setPage(lastPage);
    if (doc.internal.pageSize.getHeight() - 200 < 0) doc.addPage();
    let cy = pageH - 170;
    if (cy < 100) { doc.addPage(); cy = 60; }

    doc.setDrawColor(0, 229, 255);
    doc.setLineWidth(1.5);
    doc.rect(40, cy, pageW - 80, 120);
    doc.setFillColor(10, 10, 10);
    doc.rect(40, cy, pageW - 80, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("AKADEMIK & TEKNIK DOGRULAMA", 50, cy + 15);

    doc.setTextColor(10, 10, 10);
    doc.setLineWidth(0.5);
    doc.line(60, cy + 80, 230, cy + 80);
    doc.line(pageW - 230, cy + 80, pageW - 60, cy + 80);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("BAS MUHENDIS (Lead Engineer)", 60, cy + 94);
    doc.setFont("helvetica", "normal");
    doc.text(tr(project.engineer) || "______________________", 60, cy + 106);
    doc.setFont("helvetica", "bold");
    doc.text("AKADEMIK ONAY (Verification)", pageW - 230, cy + 94);
    doc.setFont("helvetica", "normal");
    doc.text("______________________", pageW - 230, cy + 106);

    doc.setDrawColor(255, 59, 48);
    doc.setLineWidth(2);
    doc.circle(pageW / 2, cy + 70, 28);
    doc.setFontSize(7);
    doc.setTextColor(255, 59, 48);
    doc.setFont("helvetica", "bold");
    doc.text("TS 825", pageW / 2, cy + 66, { align: "center" });
    doc.text("CERTIFIED", pageW / 2, cy + 76, { align: "center" });

    // Page numbering + footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i > 1) {
        doc.setFontSize(7);
        doc.setTextColor(82, 82, 91);
        doc.text(`Isi Yalitimi Portali · TS 825:2024 / ISO 6946 / EN 13788 · ${certId}`, 40, pageH - 24);
        doc.text(`Sayfa ${i}/${pageCount}`, pageW - 80, pageH - 24);
      }
    }

    const fname = (tr(project.project_name) || "rapor").replace(/[^a-z0-9]/gi, "_") + ".pdf";
    doc.save(fname);
    toast.success("Profesyonel PDF raporu indirildi · " + pageCount + " sayfa");
  };

  return (
    <div data-testid="export-bar" className="border border-[#D4D4D4] dark:border-[#1F1F1F] p-3 bg-white dark:bg-[#0A0A0A]">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4"/>
        <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold">
          {t("report.title", "OFFICIAL REPORT")}
        </span>
      </div>
      <Button onClick={generatePDF}
        className="w-full rounded-none bg-[var(--neon-cyan)] hover:bg-[var(--neon-cyan)]/80 text-[#0A0A0A] h-10 font-mono text-xs uppercase tracking-wider"
        data-testid="generate-pdf-btn">
        <Download className="w-4 h-4 mr-2"/> {t("report.generate", "GENERATE PDF REPORT")}
      </Button>
      <div className="mt-3 border border-[#D4D4D4] dark:border-[#1F1F1F] bg-[#F9F9FA] dark:bg-[#141414] p-2 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[var(--neon-green)]"/>
        <span className="font-mono text-[10px] uppercase tracking-wider">
          {t("report.contents", "Cover + TOC + Academic Validation")}
        </span>
      </div>
      <p className="mt-2 font-mono leading-relaxed paragraph-balanced"
        style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
        {t("report.summary", "Cover page, table of contents, materials & methodology, layer table, Glaser charts, AI expert analysis & academic validation across 6+ pages.")}
      </p>
    </div>
  );
}
